import { useQuery } from "@tanstack/react-query";
import AvailableStaff from "../models/AvailableStaff";
import OverStaffAssignation from "../models/OverStaffReport/OverStaffAssignation";
import OverStaffReport from "../models/OverStaffReport/OverStaffReport";
import AssignationService from "../services/AssignationService";
import { QueryKeyFactory } from "../services/queryKeys";
import UserService from "../services/UserService";
import { unwrap } from "../utils/queryFn";

const userKeys = new QueryKeyFactory(UserService.path);
const assignationKeys = new QueryKeyFactory(AssignationService.path);
const REPORT_CONTEXT = { scope: "over-staff-report" };
const AVAILABLE_CONTEXT = { scope: "available-staff" };
const DETAILS_CONTEXT = { scope: "over-staff-details" };
const NO_REPORT_ROWS: OverStaffReport[] = [];
const NO_AVAILABLE_ROWS: AvailableStaff[] = [];
const NO_ASSIGNATIONS: OverStaffAssignation[] = [];

/**
 * Over-staff report rows for a month. year, month, hours filter and role all belong to the cache key.
 * Pass enabled=false until the inputs are final (e.g. still being read from the URL) to avoid a throwaway request.
 */
const useOverStaffReport = (year: number, month: number, filter: string, role: string, enabled: boolean = true) => {
  const reportQuery = useQuery({
    queryKey: userKeys.list({ year, month, filter, role }, REPORT_CONTEXT),
    queryFn: () => unwrap(UserService.getOverStaffReport(year, month, filter, role)),
    enabled,
  });

  return {
    rows: reportQuery.data ?? NO_REPORT_ROWS,
    isLoading: reportQuery.isFetching,
    isError: reportQuery.isError,
    refetch: () => void reportQuery.refetch(),
  };
};

/** Staff available per week for the "Available staff" tab. */
export const useAvailableStaff = (year: number, month: number) => {
  const availableQuery = useQuery({
    queryKey: userKeys.list({ year, month }, AVAILABLE_CONTEXT),
    queryFn: () => unwrap(UserService.getAvailableStaff(year, month)),
  });

  return {
    rows: availableQuery.data ?? NO_AVAILABLE_ROWS,
    isLoading: availableQuery.isFetching,
  };
};

/** Assignations of one user, fetched only while the details dialog has a user selected. */
export const useOverStaffAssignationDetails = (userExternalId: string | undefined) => {
  const detailsQuery = useQuery({
    queryKey: assignationKeys.detail(userExternalId ?? "", DETAILS_CONTEXT),
    queryFn: () => unwrap(AssignationService.getAssignationDetails(userExternalId!)),
    enabled: !!userExternalId,
  });

  return {
    rows: detailsQuery.data ?? NO_ASSIGNATIONS,
    isLoading: detailsQuery.isFetching,
  };
};

export default useOverStaffReport;
